/**
 * Aliasy nazw modeli w URL-ach wewnętrznego API — strona nadawcza.
 *
 * Część korporacyjnych filtrów (proxy / DLP / rozszerzenia) blokuje żądania, których URL zawiera
 * słowo "gemini". Użytkownik takiej firmy widział u nas puste statystyki, mimo że dane były.
 * Kodujemy więc nazwę modelu tuż przed wysyłką; backend dekoduje ją zanim router zobaczy ścieżkę,
 * więc reszta aplikacji — po obu stronach — operuje dalej na `GEMINI`.
 *
 * Reguła jest prefiksowa, nie listą wartości: `GEMINI` → `MODEL_G`, `GEMINI_SEARCH_BROWSER` →
 * `MODEL_G_SEARCH_BROWSER`. Nowy wariant modelu jest objęty automatycznie.
 *
 * Zamieniane są wyłącznie CAŁE tokeny między separatorami URL-a (`/ ? & = , #`), więc segment
 * niosący np. domenę źródła nie ucierpi.
 *
 * Bliźniacza kopia dekodująca: `packages/llm-common/src/shared/utils/modelUrlAlias.ts` — nie możemy
 * jej stąd zaimportować, bo llm-common ściągnąłby TypeORM do bundla. Zmiana tutaj wymaga zmiany tam.
 */

const MODEL_PREFIX = 'GEMINI';
const ALIAS_PREFIX = 'MODEL_G';
// Ciąg o kształcie nazwy modelu. Celowo NIE lista separatorów: `URLSearchParams` enkoduje przecinek
// do `%2C`, więc `GEMINI_API%2CGPT_API` byłoby jednym nierozpoznawalnym tokenem.
const URL_TOKEN = /[A-Za-z0-9_]+/g;
// Kształt nazwy modelu (wielkie litery i podkreślniki) — zawężenie trzyma kodowanie z dala od fraz
// wpisanych przez użytkownika, np. `?search=Gemini w promptach`.
const MODEL_TOKEN = /^GEMINI(_[A-Z0-9_]+)?$/;
/**
 * Parametry query, które faktycznie niosą nazwy modeli. Wszystko inne — `search`, `keyword`,
 * `domain` — to tekst od użytkownika i zostaje nietknięte. Ta sama lista obowiązuje na backendzie
 * (`llm-common/shared/utils/modelUrlAlias.ts`); rozjazd którejkolwiek ze stron psuje wyszukiwanie.
 */
const MODEL_QUERY_KEYS = new Set(['models', 'chatType']);

const encodeToken = (token: string): string => {
  if (!MODEL_TOKEN.test(token)) {
    return token;
  }

  return token === MODEL_PREFIX
    ? ALIAS_PREFIX
    : `${ALIAS_PREFIX}_${token.slice(MODEL_PREFIX.length + 1)}`;
};

// Kodowanie jest wyłączone, dopóki nie wczytamy konta z addonem `MODEL_URL_ALIAS` — patrz
// `AccountContexts`. Stan modułowy, bo `apiClient` składa żądania poza drzewem Reacta i nie ma jak
// sięgnąć po kontekst. Żądania wysłane zanim konto dojedzie lecą niezakodowane i to jest poprawne:
// backend rozumie oba warianty.
let isEnabled = false;

export const setModelAliasEnabled = (enabled: boolean): void => {
  isEnabled = enabled;
};

/**
 * URL bez nazw modeli — i każdy URL, gdy addon jest wyłączony — wraca bez zmian.
 *
 * Query przetwarzamy przez `URLSearchParams`, a nie regexem po surowym stringu: `URLSearchParams`
 * enkoduje przecinek do `%2C`, przez co w `models=GEMINI_API%2CGEMINI_API_BROWSER` druga nazwa
 * skleja się z ogonem sekwencji (`2CGEMINI_API_BROWSER`) i nie daje się rozpoznać. Po sparsowaniu
 * wartości są już rozdzielone. Gdy nic się nie zmieniło, oddajemy oryginalny query string —
 * po co ryzykować, że round-trip przestawi enkodowanie czemuś, co nas nie dotyczy.
 */
export const encodeModelAliasesInUrl = (url: string): string => {
  if (!isEnabled || !url.includes(MODEL_PREFIX)) {
    return url;
  }

  const queryStart = url.indexOf('?');
  if (queryStart === -1) {
    return url.replace(URL_TOKEN, encodeToken);
  }

  const encodedPath = url.slice(0, queryStart).replace(URL_TOKEN, encodeToken);
  const rawQuery = url.slice(queryStart + 1);
  const params = new URLSearchParams(rawQuery);
  let changed = false;

  Array.from(params.entries()).forEach(([key, value]) => {
    if (!MODEL_QUERY_KEYS.has(key)) {
      return;
    }

    const encoded = value.replace(URL_TOKEN, encodeToken);
    if (encoded !== value) {
      params.set(key, encoded);
      changed = true;
    }
  });

  return `${encodedPath}?${changed ? params.toString() : rawQuery}`;
};

/**
 * To samo dla `config.params` axiosa — część serwisów podaje query obiektem zamiast wklejać je do
 * ścieżki, a wtedy nazwa modelu nie przechodzi przez `encodeModelAliasesInUrl`.
 * Mutuje w miejscu; obiekty inne niż zwykły słownik (np. `URLSearchParams`) pomija.
 */
export const encodeModelAliasesInParams = (params: unknown): void => {
  if (!isEnabled || !params || typeof params !== 'object' || Array.isArray(params)) {
    return;
  }

  const record = params as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!MODEL_QUERY_KEYS.has(key)) {
      continue;
    }

    const value = record[key];
    if (typeof value === 'string') {
      record[key] = encodeModelAliasesInUrl(value);
    } else if (Array.isArray(value)) {
      record[key] = value.map((item) =>
        typeof item === 'string' ? encodeModelAliasesInUrl(item) : item
      );
    }
  }
};
